import { useState } from "react";
import { post } from "@common/api/apiShared";
import { validateEmail } from "@common/emailValidator";
import { useDotLegalMutation, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { SelectableColoredItem } from "../../processingActivity/ProcessingActivity.types";
import { ExternalUserSaveModel } from "../ExternalUser.types";
import { ICreateExternalUserInlineFormProps } from "./CreateExternalUserInlineForm";

function useCreateExternalUser() {
    return useDotLegalMutation((model: ExternalUserSaveModel) => post<{ id: string }>("/external-users", model));
}

function normalizeEmail(email: string) {
    return email.trim().toLowerCase();
}

export function useCreateExternalUserInlineForm(props: ICreateExternalUserInlineFormProps) {
    const { translateString } = useTranslation();
    const businessAreasQuery = useDotLegalQueryWithKey<Array<SelectableColoredItem>>("selectableBusinessAreas", "/BusinessAreas/businessareas");

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [businessAreas, setBusinessAreas] = useState<Array<string>>([]);

    const createMutation = useCreateExternalUser();
    const businessAreaOptions = businessAreasQuery.data ?? [];

    // An email already in use means the person is already a possible recipient, so block the add rather than
    // creating a second registration of the same human. Which emails count is the host's call.
    const emailInUse = props.existingEmails.some((existing) => normalizeEmail(existing) === normalizeEmail(email));
    const canCreate = name.trim().length > 0 && validateEmail(email.trim()) && !emailInUse;

    const create = async () => {
        if (!canCreate) return;

        const response = await createMutation.mutateAsync({ fullName: name.trim(), email: email.trim(), businessAreas });
        if (!response.success()) return;

        props.onCreated({
            id: response.value().id,
            name: name.trim(),
            email: email.trim(),
            businessAreas: businessAreaOptions.filter((businessArea) => businessAreas.includes(businessArea.id)),
        });

        setName("");
        setEmail("");
        setBusinessAreas([]);
    };

    return {
        name,
        setName,
        email,
        setEmail,
        businessAreas,
        setBusinessAreas,
        businessAreaOptions,
        isLoadingBusinessAreas: businessAreasQuery.isLoading,
        emailErrorText: emailInUse ? translateString("userExistWithEmail") : undefined,
        canCreate,
        create,
        isCreating: createMutation.isLoading,
    };
}
